import { useEffect, useState } from 'react'
import { wizardNotify } from '../api/client'

// Optional email capture for anonymous builds (PRD-GrowthLoops 1.2): one
// field, dismissible, single submission. Rendered across both the `creating`
// and `building` phases (same tree position so it stays mounted) — a submit
// before the project ID exists is held and sent once the ID arrives.
export function NotifyEmailCapture({ projectId }: { projectId: string | null }) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'pending' | 'sent' | 'dismissed'>('idle')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (status !== 'pending' || !projectId) return
    let cancelled = false
    wizardNotify(projectId, email)
      .then(() => {
        if (!cancelled) setStatus('sent')
      })
      .catch((err) => {
        if (cancelled) return
        setStatus('idle')
        setError(err instanceof Error ? err.message : 'Could not save your email')
      })
    return () => {
      cancelled = true
    }
  }, [status, projectId, email])

  if (status === 'dismissed') return null

  return (
    <div className="fixed bottom-4 right-4 z-40 w-full max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4">
      <button
        onClick={() => setStatus('dismissed')}
        aria-label="Dismiss"
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {status === 'sent' ? (
        <p className="text-sm text-gray-700 dark:text-gray-300 pr-4">
          We&rsquo;ll email you when your build is ready.
        </p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (email.trim()) {
              setError(null)
              setStatus('pending')
            }
          }}
        >
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 pr-4">
            Drop your email and we&rsquo;ll send you the link when it&rsquo;s ready.
          </p>
          <div className="flex gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 min-w-0 px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <button
              type="submit"
              disabled={status === 'pending'}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-500 disabled:opacity-50 transition-colors"
            >
              Notify me
            </button>
          </div>
          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </form>
      )}
    </div>
  )
}
